import { FORM_CONTAINER_SX, fieldSpanSx } from './JsonFormsTheme';

/**
 * A section's 12-column grid of controls, which is also the box its controls' `@container` rules
 * measure: the group's own width is what a control actually has to fit in. GridGroupRenderer lays
 * a generated section out in it; a hand-written page lays its fields out in the same one, so both
 * look alike (JSON-Forms-Grid-Layout.md, Hand-Written Pages). `gap` is the density's `gridGap`.
 */
export const formFieldGridSx = (gap: number) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  gap,
  alignContent: 'start',
  width: '100%',
  ...FORM_CONTAINER_SX,
});

/**
 * One control's cell in {@link formFieldGridSx}: `span` of the 12 columns, widened a step at a time
 * as the grid narrows ({@link fieldSpanSx}). A field the layout gives no width takes `span 2`, a sixth
 * of the panel, and long text `span 4` (the generator's defaultFieldWidth). A value object's block
 * passes the number of `parts` on its row, so it widens that many controls at a time.
 */
export const formFieldCellSx = (span: number, narrow = false, parts = 1) => ({
  gridColumn: `span ${span}`,
  ...fieldSpanSx(span, narrow, parts),
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

/** A ui schema element as the grid reads it: its span, whether it is narrow, and a value object block's parts. */
interface GridElement {
  type: string;
  options?: { gridColumn?: string; valueObject?: boolean; narrow?: boolean };
  elements?: GridElement[];
}

const spanOf = (element: GridElement): number =>
  Number(/span\s+(\d+)/.exec(element.options?.gridColumn ?? '')?.[1] ?? 12);

const isValueObjectBlock = (element: GridElement): boolean =>
  element.type === 'Group' && element.options?.valueObject === true;

/** The parts on a value object block's first row. */
function firstRowParts(element: GridElement): GridElement[] {
  const parts: GridElement[] = [];
  let used = 0;
  for (const part of element.elements ?? []) {
    used += spanOf(part);
    if (used > 12) break;
    parts.push(part);
  }
  return parts;
}

/**
 * How a control or a value object's block steps in {@link formFieldCellSx}: a block by the number of controls on its
 * first row, and as a narrow control when all of them are narrow, so its parts stay as wide as the controls beside it.
 */
export function cellStepsOf(element: GridElement): { narrow: boolean; parts: number } {
  if (!isValueObjectBlock(element)) return { narrow: element.options?.narrow === true, parts: 1 };
  const parts = firstRowParts(element);
  return {
    narrow: parts.length > 0 && parts.every((part) => part.options?.narrow === true),
    parts: Math.max(1, parts.length),
  };
}
